/**
 * Rows in a team conversation that are about agents rather than people: what happened to the roster (added, blocked,
 * removed), the directory of shared agents put on the table when nobody fits a request, and a request waiting for its owner.
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldAlert, Users, Check, X } from 'lucide-react';
import { spacesAPI } from '../../services/api.js';
import { talkTo } from './useAgentSession.js';

const T = {
  surface: 'var(--lyra-color-surface)', surface2: 'var(--lyra-color-surface-2)', border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)', ink3: 'var(--lyra-color-text-muted)', ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)', accentLight: 'var(--app-primary-soft)', accentMid: 'var(--app-primary-soft-2)',
  danger: 'var(--lyra-color-danger)', dangerBg: 'var(--lyra-color-danger-bg)',
};
const card = { border: `1px solid ${T.border}`, background: T.surface, borderRadius: 14, padding: '12px 14px', maxWidth: 'min(560px, 92%)' };
const pill = (on) => ({ fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: on ? T.accentLight : T.surface2, color: on ? T.accent : T.ink3, border: `1px solid ${on ? T.accentMid : T.border}` });
const btn = (primary) => ({ border: primary ? 'none' : `1px solid ${T.border}`, background: primary ? T.accent : T.surface, color: primary ? 'var(--app-on-accent)' : T.ink, borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });


/**
 * One cached read of the team's roster, shared by every card in the thread (a long chat has many). Refreshed when the room announces a
 * change (the feed forwards the stream's `roster_update` as a window event) and at most every few seconds otherwise.
 */
const rosterCache = new Map();   // spaceId -> { at, promise, data }
const ROSTER_TTL_MS = 4000;
export function fetchRoster(spaceId, { force = false } = {}) {
  const hit = rosterCache.get(String(spaceId));
  if (hit && !force && Date.now() - hit.at < ROSTER_TTL_MS) return hit.promise;
  const promise = spacesAPI.agents(spaceId).then((d) => { const e = rosterCache.get(String(spaceId)); if (e) e.data = d; return d; });
  rosterCache.set(String(spaceId), { at: Date.now(), promise, data: hit?.data || null });
  promise.catch(() => rosterCache.delete(String(spaceId)));
  return promise;
}
/** The roster for a card, kept fresh while `poll` is true (a request is still waiting). */
export function useRoster(spaceId, poll = false) {
  const [data, setData] = useState(() => rosterCache.get(String(spaceId))?.data || null);
  useEffect(() => {
    let live = true;
    const load = (force) => fetchRoster(spaceId, { force }).then((d) => { if (live) setData(d); }).catch(() => {});
    load(false);
    const onChange = () => load(true);
    window.addEventListener('lyra:roster-update', onChange);
    const timer = poll ? setInterval(() => load(false), 8000) : null;
    return () => { live = false; window.removeEventListener('lyra:roster-update', onChange); if (timer) clearInterval(timer); };
  }, [spaceId, poll]);
  return data;
}

const ask = (e, fallback) => toast.error(e?.response?.data?.message || fallback);

const wrap = (children) => <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 14px' }}>{children}</div>;

/** A line in the thread: "Ann shared X", "Olly blocked X: reason…". The person who shared an agent can change how it is shared right there. */
export function AgentEventRow({ activity, spaceId, meId }) {
  const meta = activity.meta || {};
  const bad = ['blocked', 'removed', 'lyran-blocked'].includes(meta.kind);
  const mine = ['added', 'access'].includes(meta.kind) && meta.rosterId && String(meta.ownerUserId) === String(meId);
  const roster = useRoster(spaceId, false);
  const entry = mine && roster ? (roster.agents || []).find((a) => String(a._id) === String(meta.rosterId)) : null;
  const [busy, setBusy] = useState(false);
  const change = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); window.dispatchEvent(new Event('lyra:roster-update')); }
    catch (e) { ask(e, 'Could not change that'); }
    finally { setBusy(false); }
  };
  return wrap(
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 7, maxWidth: '92%' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: bad ? T.danger : T.ink3, background: bad ? T.dangerBg : T.surface2, border: `1px solid ${bad ? 'transparent' : T.border}`, borderRadius: 99, padding: '5px 12px' }}>
        {bad ? <ShieldAlert size={13} /> : <Users size={13} />}
        <span>{activity.content}</span>
      </div>
      {entry && entry.status === 'active' && (
        <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <span style={{ fontSize: 11.5, color: T.ink4 }}>Who can use {entry.name}?</span>
          <button type="button" disabled={busy || entry.access === 'open'} style={{ ...btn(entry.access === 'open'), opacity: entry.access === 'open' ? 1 : 0.95 }}
            onClick={() => change(() => spacesAPI.setAgentAccess(spaceId, entry._id, 'open'), 'Open to everyone')}>Anyone, right away</button>
          <button type="button" disabled={busy || entry.access === 'ask'} style={btn(entry.access === 'ask')}
            onClick={() => change(() => spacesAPI.setAgentAccess(spaceId, entry._id, 'ask'), 'You approve each use')}>Only if I approve</button>
          <button type="button" disabled={busy} style={btn(false)}
            onClick={() => change(() => spacesAPI.removeAgent(spaceId, entry._id), 'No longer shared')}>Stop sharing</button>
        </div>
      )}
    </div>
  );
}

/**
 * The agents the team has shared, with the right button for each: yours (nothing to do), open (use it now), or ask-first (ask its owner,
 * then it waits for them). It checks the live roster, so an agent blocked or removed since the card was posted is not offered.
 */
export function DirectoryCard({ activity, spaceId, meId }) {
  const agents = activity.meta?.agents || [];
  const roster = useRoster(spaceId, true);
  const live = roster ? new Map((roster.agents || []).map((r) => [String(r._id), r])) : null;
  const waiting = new Set((roster?.requests || []).filter((r) => String(r.requesterId) === String(meId)).map((r) => String(r.agentId)));
  const approved = new Set((roster?.grants || []).map((g) => String(g.agentId)));

  // One click starts a conversation: a pill appears above the message box and the next messages go to this agent, no @ needed. For an
  // ask-first agent the first message asks its owner (that is what the card in the thread then shows), and the approval covers the conversation.
  const start = (a, isOpen) => {
    talkTo(spaceId, { agentId: a.agentId, name: a.name, access: isOpen ? 'open' : 'ask', owner: a.owner, ownerUserId: a.ownerUserId, payer: (live && live.get(String(a.rosterId))?.payer) || a.payer || 'owner' });
    toast.success(isOpen ? `Talking to ${a.name}. Type your request.` : `Talking to ${a.name}. Your first message goes to ${a.owner} for approval.`);
  };

  return wrap(
    <div style={card}>
      <div style={{ fontSize: 12.5, color: T.ink, marginBottom: 8, lineHeight: 1.5 }}>{activity.content}</div>
      {agents.map((a) => {
        const now = live ? live.get(String(a.rosterId)) : undefined;
        const gone = live && (!now || now.status !== 'active');
        const mine = String(a.ownerUserId) === String(meId);
        const access = now?.access || a.access;
        const isOpen = access === 'open';
        const requested = waiting.has(String(a.agentId));
        const covered = approved.has(String(a.agentId));
        return (
          <div key={a.rosterId} style={{ borderTop: `1px solid ${T.border}`, padding: '8px 0', opacity: gone ? 0.55 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <b style={{ fontSize: 13, color: T.ink }}>{a.name}</b>
              <span style={pill(isOpen)}>{isOpen ? 'Open: use it now' : 'Ask first: owner approves'}</span>
              <span style={{ fontSize: 11.5, color: T.ink4 }}>{mine ? 'yours' : `by ${a.owner}`}</span>
              <span title={(now?.payer || a.payer) === 'requester' ? 'You confirm each run first, and your keys pay. Only your request is sent to your provider.' : undefined} style={{ fontSize: 11.5, color: (now?.payer || a.payer) === 'requester' ? T.accent : T.ink4 }}>
                {(now?.payer || a.payer) === 'requester' ? (mine ? 'tokens: whoever uses it' : 'tokens: you cover (you confirm)') : `tokens: ${mine ? 'you' : a.owner}`}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                {gone ? <span style={{ fontSize: 11.5, color: T.ink4 }}>No longer shared</span>
                  : mine ? <button type="button" style={btn(true)} onClick={() => start(a, true)}>Talk to it</button>
                  : requested ? <span style={pill(false)}>Waiting for {a.owner}</span>
                  : isOpen || covered ? <button type="button" style={btn(true)} onClick={() => start(a, true)}>{covered && !isOpen ? 'Continue' : 'Use'}</button>
                  : <button type="button" style={btn(false)} onClick={() => start(a, false)}>Ask owner</button>}
              </span>
            </div>
            {a.description && <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>{a.description}</div>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A request to use someone's agent. EVERYONE sees where it stands (waiting, approved, declined); only the agent's owner sees the buttons, and
 * only while it is waiting.
 */
const STATE = { pending: ['Waiting for the owner to approve', 'accent'], approved: ['Approved', 'ok'], declined: ['Declined', 'bad'], cancelled: ['Cancelled', 'plain'], done: ['Approved and answered', 'ok'], failed: ['Approved, but the run failed', 'bad'] };
export function RequestCard({ activity, spaceId, meId }) {
  const requestId = String(activity.meta?.requestId || '');
  const ownerId = String(activity.meta?.ownerUserId || '');
  const mine = ownerId === String(meId);
  const first = useRoster(spaceId, true);
  const found = first?.requestStatuses?.[requestId];   // an old request that has scrolled out of the recent list simply shows no badge
  const status = found ? found.status : null;
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    setBusy(true);
    try {
      if (action === 'decline') await spacesAPI.declineAgentRequest(spaceId, requestId);
      else await spacesAPI.approveAgentRequest(spaceId, requestId, action === 'always');
      window.dispatchEvent(new Event('lyra:roster-update'));
    } catch (e) { ask(e, 'Could not save your decision'); }
    finally { setBusy(false); }
  };
  const [label, tone] = STATE[status] || ['', 'plain'];
  return wrap(
    <div style={card}>
      <div style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.5 }}>{activity.content}</div>
      {status && <div style={{ marginTop: 8 }}><span style={{ ...pill(tone === 'accent' || tone === 'ok'), ...(tone === 'bad' ? { background: T.dangerBg, color: T.danger, border: '1px solid transparent' } : {}) }}>{label}</span></div>}
      {mine && status === 'pending' && (
        <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
          <button type="button" style={btn(true)} disabled={busy} onClick={() => decide('once')}><Check size={12} /> Approve once</button>
          <button type="button" style={btn(false)} disabled={busy} onClick={() => decide('always')}>Always allow the team</button>
          <button type="button" style={btn(false)} disabled={busy} onClick={() => decide('decline')}><X size={12} /> Decline</button>
        </div>
      )}
      {!mine && status === 'pending' && <div style={{ fontSize: 11, color: T.ink4, marginTop: 6 }}>Only the agent’s owner can approve this.</div>}
    </div>
  );
}

/**
 * "This needs someone to cover the tokens." For a person asking for an agent that whoever uses it pays for, this is the one question they are
 * asked: cover my own messages to it for the next 30 minutes (pay only for what is used, up to a limit they pick), after which nothing more is
 * asked. For a teammate it is "sponsor this one request". Only the live ones show buttons.
 */
const LIMITS = [
  { tokens: 3000, label: 'a reply or two' },
  { tokens: 10000, label: 'a short conversation' },
  { tokens: 30000, label: 'a long one' },
];
const kfmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 ? 1 : 0)}k` : String(n));

export function SponsorCard({ activity, spaceId, meId }) {
  const requestId = activity.meta?.requestId;
  const requesterId = activity.meta?.requesterId;
  const typical = activity.meta?.typicalTokens;
  const roster = useRoster(spaceId, true);
  const state = roster ? ((roster.sponsorRequests || []).some((x) => String(x._id) === String(requestId)) ? 'pending' : 'closed') : null;
  const choices = LIMITS.some((l) => l.tokens === activity.meta?.capTokens) || !activity.meta?.capTokens ? LIMITS : [...LIMITS, { tokens: activity.meta.capTokens, label: 'suggested' }].sort((a, b) => a.tokens - b.tokens);
  const [cap, setCap] = useState(activity.meta?.capTokens || 10000);
  const [busy, setBusy] = useState(false);
  const mine = String(requesterId) === String(meId);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); window.dispatchEvent(new Event('lyra:roster-update')); }
    catch (e) { toast.error(e?.response?.data?.message || 'Could not save that'); }
    finally { setBusy(false); }
  };
  return wrap(
    <div style={{ ...card, borderColor: T.accentMid, background: T.accentLight }}>
      <div style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.5 }}>{activity.content}</div>
      {state === 'pending' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: T.ink3, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {mine ? 'Limit' : 'Up to'}
              <select value={cap} onChange={(e) => setCap(Number(e.target.value))} aria-label="Token limit" style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: '5px 8px', fontSize: 12.5, background: T.surface, color: T.ink }}>
                {choices.map((c) => <option key={c.tokens} value={c.tokens}>{kfmt(c.tokens)} tokens · {c.label}</option>)}
              </select>
            </label>
            <button type="button" style={btn(true)} disabled={busy} onClick={() => act(() => spacesAPI.approveSponsorRequest(spaceId, requestId, cap), mine ? 'Covering your messages' : 'Sponsoring this run')}>
              {mine ? 'Cover my messages' : 'Sponsor this run'}
            </button>
            <button type="button" style={btn(false)} disabled={busy} onClick={() => act(() => spacesAPI.declineSponsorRequest(spaceId, requestId), mine ? 'Withdrawn' : 'Declined')}>
              {mine ? 'Withdraw' : 'Not now'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: T.ink4, marginTop: 7, lineHeight: 1.5 }}>
            {mine
              ? `You pay only for what is used, never more than the limit, and only for your own messages to this agent. ${typical ? `A reply from it has been using about ${kfmt(typical)} tokens. ` : ''}Nothing is charged to anyone else.`
              : `A run of this agent has been using about ${typical ? kfmt(typical) : 'a few thousand'} tokens. Expires in 30 minutes. You pay only for what is used, up to the limit.`}
          </div>
        </>
      )}
    </div>
  );
}
